import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { themeOrange, themeBlue } from "../../../utils/colorTheme";
import Input from "../../../component/common/Input";
import { useDispatch, useSelector } from "react-redux";
import { asyncStatus } from "../../../utils/asyncStatus";
import { getUserByIdAsync } from "../../../services/DashboardUsers";
import { apiHandle } from "../../../config/apiHandle/apiHandle";
import { success_toast_message, error_toast_message } from "../../../utils/toast_message";
import { IoArrowBack } from "react-icons/io5";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { get_user_by_id_status, get_user_by_id_data } = useSelector(
    (state) => state.dashboard_users
  );

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    dispatch(getUserByIdAsync(id));
  }, [id]);

  useEffect(() => {
    if (get_user_by_id_data) {
      setFormData({
        first_name: get_user_by_id_data.first_name || "",
        last_name: get_user_by_id_data.last_name || "",
        email: get_user_by_id_data.email || "",
        phone: get_user_by_id_data.phone || "",
      });
    }
  }, [get_user_by_id_data]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email) {
      error_toast_message("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await apiHandle.post(`/admin/user-add-edit/${id}`, formData);
      if (response.data.status) {
        success_toast_message("User updated successfully");
        navigate(-1);
      } else {
        error_toast_message(response.data.message || "Failed to update user");
      }
    } catch (error) {
      error_toast_message(error?.response?.data?.message || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    borderRadius: "10px",
    border: "1px solid #e0e0e0",
    boxShadow: "none",
    fontSize: "14px",
  };

  if (get_user_by_id_status === asyncStatus.LOADING) {
    return (
      <Stack alignItems="center" justifyContent="center" height="60vh">
        <CircularProgress size={30} sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  return (
    <Stack sx={{ bgcolor: "#F8F9FA", minHeight: "100vh" }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" gap={2} sx={{ px: 3, py: 2.5 }}>
        <Box
          onClick={() => navigate(-1)}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "1px solid #f0f0f0",
            transition: "all 0.15s",
            "&:hover": { borderColor: "#ddd", bgcolor: "#f5f5f5" },
          }}
        >
          <IoArrowBack size={16} color="#555" />
        </Box>
        <Stack>
          <Typography sx={{ fontSize: "24px", fontWeight: 700, color: themeBlue }}>
            Edit User
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#999", mt: 0.3 }}>
            Update user information
          </Typography>
        </Stack>
      </Stack>

      {/* Form */}
      <Container sx={{ maxWidth: { xl: "md", lg: "md", md: "md", sm: "sm", xs: "xs" } }}>
        <Stack
          sx={{
            bgcolor: "#fff",
            borderRadius: "14px",
            boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            p: { xs: 3, sm: 4 },
            mb: 4,
          }}
        >
          <Stack gap={3}>
            <Stack gap={0.5}>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: themeBlue }}>
                Personal Information
              </Typography>
              <Box sx={{ height: "1px", bgcolor: "#f0f0f0", mt: 1 }} />
            </Stack>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Stack gap={0.5}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                    First Name
                  </Typography>
                  <Input
                    type="text"
                    onChange={(e) => handleChange("first_name", e.target.value)}
                    style={inputStyle}
                    placeholder="Enter first name"
                    value={formData.first_name}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack gap={0.5}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                    Last Name
                  </Typography>
                  <Input
                    type="text"
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    style={inputStyle}
                    placeholder="Enter last name"
                    value={formData.last_name}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack gap={0.5}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                    Email
                  </Typography>
                  <Input
                    type="email"
                    onChange={(e) => handleChange("email", e.target.value)}
                    style={inputStyle}
                    placeholder="Enter email"
                    value={formData.email}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack gap={0.5}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                    Phone
                  </Typography>
                  <Input
                    type="text"
                    onChange={(e) => handleChange("phone", e.target.value)}
                    style={inputStyle}
                    placeholder="Enter phone number"
                    value={formData.phone}
                  />
                </Stack>
              </Grid>
            </Grid>

            {/* Submit */}
            <Stack direction="row" justifyContent="flex-end" mt={2}>
              <Box
                onClick={() => navigate(-1)}
                sx={{
                  px: 3,
                  py: 1.3,
                  borderRadius: "10px",
                  border: "1px solid #e0e0e0",
                  cursor: "pointer",
                  mr: 1.5,
                  transition: "all 0.15s",
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#666" }}>
                  Cancel
                </Typography>
              </Box>
              <Box
                onClick={handleSubmit}
                sx={{
                  px: 4,
                  py: 1.3,
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #F15A24 0%, #C53F10 100%)",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                  transition: "all 0.2s",
                  "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(241,90,36,0.35)" },
                }}
              >
                {loading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}>
                    Update User
                  </Typography>
                )}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
};

export default EditUser;
